import { Box, ButtonProps, Divider, Stack } from "@mui/material";
import React from "react";
import { createState } from "src/utils/create_zustand_store";
import {
  DynamicModal,
  DynamicModalCloseIconProps,
} from "src/components/base/components/DynamicModal";
import { ModalActionButtonV2 } from "src/components/base/components/ModalActionButtonV2";

const genericModalInitialState = {
  isOpen: false,
  isClosing: false,
  isConfirming: false,
  title: "" as string | React.ReactNode,
  description: "" as string | React.ReactNode,
  titleIconVariant: "" as "error" | "warning" | "info" | "success",
  hideTitleIcon: false,
  confirmButtonText: "" as string | React.ReactNode,
  confirmButtonProps: {} as ButtonProps,
  closeButtonText: "" as string | React.ReactNode,
  isCloseIconHidden: false,
  isCloseButtonHidden: false,
  isConfirmButtonHidden: false,
  closeButtonProps: {} as ButtonProps,
  onClickClose: (() => {}) as () => void,
  // Must stay undefined by default. DynamicModal only falls back to its own
  // onClose handler when slot.closeIcon.onClick is falsy — a no-op function
  // here is truthy, so it permanently shadowed that fallback and left the X
  // dead on every modal that did not wire the icon explicitly.
  onClickCloseIcon: undefined as (() => void) | undefined,
  onClickConfirm: (() => {}) as () => void,
  onClickConfirmWithoutModalClose: null as (() => void) | null,

  closeIconProps: {} as DynamicModalCloseIconProps,
};

type TGenericModalState = typeof genericModalInitialState;

const useGenericModalState = createState<TGenericModalState>(
  genericModalInitialState
);

// reset() only flips isOpen off immediately; the full state wipe is deferred so
// the modal can play its close animation with its content still mounted. That
// pending wipe MUST be cancellable: flows that chain modals (create listing ->
// pricing strategy -> success) open the next one inside the 400ms window, and an
// uncancelled wipe would close the modal that was just opened, leaving the
// caller's state machine stuck. Module-level because the store is a singleton.
let pendingResetTimer: ReturnType<typeof setTimeout> | null = null;

const cancelPendingReset = () => {
  if (pendingResetTimer) {
    clearTimeout(pendingResetTimer);
    pendingResetTimer = null;
  }
};

export const useGenericModal = () => {
  const state = useGenericModalState();

  const set = (newState: Partial<TGenericModalState>) => {
    if (newState?.isOpen) {
      // Opening a modal supersedes the previous one's pending teardown, and
      // clears the transient button flags it left behind (otherwise the new
      // modal renders with its buttons disabled/spinning).
      cancelPendingReset();
      state.set({ isClosing: false, isConfirming: false, ...newState });
      return;
    }
    state.set(newState);
  };

  const reset = () => {
    cancelPendingReset();
    state.set({ isOpen: false });
    pendingResetTimer = setTimeout(() => {
      pendingResetTimer = null;
      state.reset();
    }, 400);
  };

  return { ...state, set, reset };
};

export function GenericModal() {
  const props = useGenericModal();

  const onCloseModal = async () => {
    props.set({ isClosing: true });
    await props?.onClickClose?.();
    props.reset();
  };

  const onConfirmModal = async () => {
    if (props?.onClickConfirmWithoutModalClose) {
      return await props?.onClickConfirmWithoutModalClose?.();
    }

    props.set({ isConfirming: true });
    await props?.onClickConfirm?.();
    props.reset();
  };

  return (
    <DynamicModal
      isOpen={props.isOpen}
      onClose={onCloseModal}
      titleIconVariant={props.titleIconVariant || "info"}
      hideTitleIcon={props?.hideTitleIcon}
      title={props?.title || <Box height={24} />}
      hideCloseIcon={props?.isCloseIconHidden}
      slot={{
        closeIcon: {
          onClick: props?.onClickCloseIcon,
          ...props?.closeIconProps,
        },
      }}
      renderBody={() => {
        return (
          <Box>
            <Box p={3}>{props?.description}</Box>
          </Box>
        );
      }}
      renderFooter={() => {
        return (
          <Stack
            p={2}
            direction={"row"}
            justifyContent={"flex-end"}
            spacing={2}
          >
            {!props?.isCloseButtonHidden ? (
              <ModalActionButtonV2
                isLoading={props?.isClosing}
                isDisabled={props?.isClosing || props?.isConfirming}
                variant="primary"
                onClick={onCloseModal}
                style={{ minWidth: "120px", textTransform: "capitalize" }}
                {...(props?.closeButtonProps as any)}
              >
                {props?.closeButtonText || "Close"}
              </ModalActionButtonV2>
            ) : null}

            {!props?.isConfirmButtonHidden ? (
              <ModalActionButtonV2
                isLoading={props?.isConfirming}
                isDisabled={props?.isClosing || props?.isConfirming}
                variant="primary"
                onClick={onConfirmModal}
                style={{ minWidth: "120px", textTransform: "capitalize" }}
                {...(props?.confirmButtonProps as any)}
              >
                {props?.confirmButtonText || "Confirm"}
              </ModalActionButtonV2>
            ) : null}
          </Stack>
        );
      }}
    />
  );
}
